import { useFormik } from "formik";
import { find, get } from "lodash";
import moment from "moment";
import { Fragment, useMemo } from "react";
import {
  CONTACT_POINT_SYSTEM,
  CURRENCY_SYMBOL,
  DATE_FORMAT,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";
import { isAcorisNonResponsiblePlan } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";
import {
  buildAcorisBeneficiaries,
  shouldShowAcorisBeneficiariesSection,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonDisplay";
import { costToFrench } from "utils/helpers/values";
import cakeIcon from "../Assets/icons/cake.svg";
import diversityIcon from "../Assets/icons/diversity.svg";
import editSquareIcon from "../Assets/icons/edit_square.svg";
import eventUpcomingIcon from "../Assets/icons/event_upcoming.svg";
import faceIcon from "../Assets/icons/face.svg";
import idCardIcon from "../Assets/icons/id_card.svg";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Checkbox from "../Components/Input/Checkbox";
import Layout from "../Components/Layout";
import DocumentDownload from "./DocumentDownload";
import { buildRecapOfferSections } from "./recapOffer";

function View({
  subscription,
  onSubmit,
  generalCost,
  updateProjectStatus,
  attachments,
  downloadFile,
  documentsLoading,
  onBack,
  translate,
}) {
  const currency = CURRENCY_SYMBOL[get(generalCost, "currency")] || "€";
  const toCurrencySymbol = (code) => CURRENCY_SYMBOL[code] || currency;
  const baseCost = Number(get(generalCost, "cost", 0)) || 0;
  const nonResponsiblePlan = isAcorisNonResponsiblePlan(
    get(subscription, "insurancePlan"),
  );
  const upsells = get(
    subscription,
    `project.fields.upsells.${get(subscription, "insurancePlan.id")}`,
    [],
  );
  const offerSections = buildRecapOfferSections({
    formuleName:
      get(generalCost, "insurancePlan.name") ||
      get(subscription, "insurancePlan.name"),
    formuleCost: baseCost,
    upsells,
  });

  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  );

  const getTelecom = (system) =>
    find(get(subscription, "project.contact.telecoms", []), { system });

  const addressValue = get(
    getTelecom(CONTACT_POINT_SYSTEM.ADDRESS),
    "value",
    "",
  );
  const cityValue = get(getTelecom(CONTACT_POINT_SYSTEM.CITY), "value", "");
  const zipValue = get(getTelecom(CONTACT_POINT_SYSTEM.ZIP_CODE), "value", "");
  const countryValue = get(
    getTelecom(CONTACT_POINT_SYSTEM.COUNTRY),
    "value",
    "",
  );

  const beneficiaries = buildAcorisBeneficiaries({
    relatedPersons,
    translate: translate?.BENEFICIARIES,
  });
  const showBeneficiariesSection = shouldShowAcorisBeneficiariesSection({
    beneficiaries,
  });

  const EDIT_BLOCK = [
    {
      icon: faceIcon,
      label: translate?.EDIT_BLOCK?.YOU_ARE,
      value: `${get(subscription, "project.contact.user.firstname")} ${get(
        subscription,
        "project.contact.user.lastname",
      )}`,
      template: TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_ACORIS].ADHERENT_INFO,
      isBeforeLocked: true,
    },
    {
      icon: cakeIcon,
      label: translate?.EDIT_BLOCK?.BIRTH_DATE,
      value: moment(get(subscription, "project.contact.user.birthDate")).format(
        DATE_FORMAT,
      ),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_ACORIS].ADHERENT_BIRTHDATE,
      isBeforeLocked: true,
    },
    {
      icon: idCardIcon,
      label: translate?.EDIT_BLOCK?.POSTAL_ADDRESS,
      value: (
        <>
          <span>{addressValue}</span>
          {(cityValue || zipValue || countryValue) && (
            <span>
              {cityValue}
              {cityValue && zipValue ? " - " : ""}
              {zipValue}
              {countryValue
                ? `${cityValue || zipValue ? " - " : ""}${countryValue}`
                : ""}
            </span>
          )}
        </>
      ),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_ACORIS].ADHERENT_TELECOMS,
    },
    ...(showBeneficiariesSection
      ? [
          {
            icon: diversityIcon,
            label: translate?.EDIT_BLOCK?.BENEFICIARIES,
            value: beneficiaries.map(({ name, label }) => (
              <span key={`${name || "empty"}-${label}`}>
                <strong>{name}</strong>
                {label ? ` ${label}` : ""}
              </span>
            )),
            template:
              TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_ACORIS].RELATED_PERSONS,
          },
        ]
      : []),
  ];

  const subscriptionId = get(subscription, "id");
  const initialValues = useMemo(
    () => ({
      additionalInfo: get(subscription, "additionalInfo"),
      project: {
        fields: {
          acceptProductStatuses: get(
            subscription,
            "project.fields.acceptProductStatuses",
            false,
          ),
          acceptAgreement: get(
            subscription,
            "project.fields.acceptAgreement",
            false,
          ),
          acceptDocumentsReceived: get(
            subscription,
            "project.fields.acceptDocumentsReceived",
            false,
          ),
          acceptFees: get(subscription, "project.fields.acceptFees", false),
        },
      },
    }),
    // Seed once per subscription — post-submit refetch must not rewrite
    // Formik initialValues (that used to loop with validateOnMount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionId],
  );

  const { submitForm, values, setFieldValue } = useFormik({
    initialValues,
    onSubmit,
  });

  const consentCheckboxes = useMemo(
    () => [
      ...(nonResponsiblePlan
        ? [
            {
              field: "project.fields.acceptProductStatuses",
              label: translate?.CONSENT?.NON_RESPONSIBLE,
            },
          ]
        : []),
      {
        field: "project.fields.acceptAgreement",
        label: translate?.CONSENT?.DATA_PROCESSING,
      },
      {
        field: "project.fields.acceptDocumentsReceived",
        label: translate?.CONSENT?.DOCUMENTS_RECEIVED,
      },
      {
        field: "project.fields.acceptFees",
        label: translate?.CONSENT?.ELECTRONIC,
      },
    ],
    [nonResponsiblePlan, translate],
  );

  const allConsentsAccepted = consentCheckboxes.every(({ field }) =>
    Boolean(get(values, field)),
  );

  const handleEdit = (template, isBeforeLocked = false) => {
    updateProjectStatus({ template, isBeforeLocked });
  };

  return (
    <Layout>
      <Header
        title={translate?.HEADER?.TITLE}
        highlight={translate?.HEADER?.HIGHLIGHT}
        subtitle={translate?.HEADER?.SUBTITLE}
      />

      <div className="acoris-recap__wrapper">
        <div className="acoris-recap__columns">
          <div className="acoris-recap__column">
            <div className="acoris-recap__section">
              <p className="acoris-recap__section-label">
                {translate?.OFFER?.FORMULE || translate?.OFFER?.LABEL}
              </p>
              <div className="acoris-recap__offer-card">
                <h3 className="acoris-recap__offer-title">
                  {offerSections.formule.name}
                </h3>
                <div className="acoris-recap__offer-price">
                  <div className="acoris-recap__offer-price--main">
                    <span className="acoris-recap__offer-price--amount">
                      {costToFrench(offerSections.formule.cost)}
                    </span>
                    <span className="acoris-recap__offer-price--unit">
                      <span>{currency}</span>
                      <span>{translate?.OFFER?.PER_MONTH}</span>
                    </span>
                  </div>
                  <p className="acoris-recap__offer-price--yearly">
                    {translate?.OFFER?.YEARLY?.replace(
                      "{amount}",
                      costToFrench(offerSections.formule.cost * 12),
                    )?.replace("{currency}", currency)}
                  </p>
                </div>
              </div>
            </div>

            {offerSections.pack && (
              <div className="acoris-recap__section">
                <p className="acoris-recap__section-label">
                  {translate?.OFFER?.PACK}
                </p>
                <div className="acoris-recap__upsells-card">
                  <div className="acoris-recap__upsell-row">
                    <span className="acoris-recap__upsell-name">
                      {offerSections.pack.name}
                    </span>
                    <div className="acoris-recap__upsell-price">
                      <div className="acoris-recap__upsell-price--main">
                        <span className="acoris-recap__upsell-price--amount">
                          {costToFrench(offerSections.pack.cost)}{" "}
                          {toCurrencySymbol(offerSections.pack.currency)}
                        </span>
                        <span className="acoris-recap__upsell-price--period">
                          {translate?.OFFER?.PER_MONTH}
                        </span>
                      </div>
                      <span className="acoris-recap__upsell-price--yearly">
                        {translate?.OFFER?.YEARLY_SHORT?.replace(
                          "{amount}",
                          costToFrench(offerSections.pack.cost * 12),
                        )?.replace(
                          "{currency}",
                          toCurrencySymbol(offerSections.pack.currency),
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {offerSections.renforts.length > 0 && (
              <div className="acoris-recap__section">
                <p className="acoris-recap__section-label">
                  {offerSections.renforts.length > 1
                    ? translate?.OFFER?.REINFORCEMENTS
                    : translate?.OFFER?.RENFORT}
                </p>
                <div className="acoris-recap__upsells-card">
                  {offerSections.renforts.map(
                    ({ name, cost, currency: upsellCurrency }, index) => {
                      const upsellCost = Number(cost) || 0;
                      const displayCurrency = toCurrencySymbol(upsellCurrency);
                      return (
                        <Fragment key={name || index}>
                          {index > 0 && (
                            <hr className="acoris-recap__upsell-divider" />
                          )}
                          <div className="acoris-recap__upsell-row">
                            <span className="acoris-recap__upsell-name">
                              {name}
                            </span>
                            <div className="acoris-recap__upsell-price">
                              <div className="acoris-recap__upsell-price--main">
                                <span className="acoris-recap__upsell-price--amount">
                                  {costToFrench(upsellCost)} {displayCurrency}
                                </span>
                                <span className="acoris-recap__upsell-price--period">
                                  {translate?.OFFER?.PER_MONTH}
                                </span>
                              </div>
                              <span className="acoris-recap__upsell-price--yearly">
                                {translate?.OFFER?.YEARLY_SHORT?.replace(
                                  "{amount}",
                                  costToFrench(upsellCost * 12),
                                )?.replace("{currency}", displayCurrency)}
                              </span>
                            </div>
                          </div>
                        </Fragment>
                      );
                    },
                  )}
                </div>
              </div>
            )}

            <div className="acoris-recap__section">
              <p className="acoris-recap__section-label">
                {translate?.DOCUMENTS?.TITLE}
              </p>
              <DocumentDownload
                items={attachments}
                download={(id) => downloadFile(id)}
                downloadLabel={translate?.DOCUMENTS?.DOWNLOAD}
              />
            </div>
          </div>

          <div className="acoris-recap__vertical-divider" />

          <div className="acoris-recap__column">
            <div className="acoris-recap__info-section">
              <div className="acoris-recap__section acoris-recap__section--spaced">
                <p className="acoris-recap__section-label">
                  {translate?.INFO?.ADHERENT_TITLE}
                </p>
                <div className="acoris-recap__info-list">
                  {EDIT_BLOCK.map(
                    ({
                      icon,
                      label,
                      value,
                      template,
                      isBeforeLocked = false,
                    }) => (
                      <div className="acoris-recap__info-item" key={label}>
                        <div className="acoris-recap__info-main">
                          <img
                            src={icon}
                            alt=""
                            className="acoris-recap__info-icon"
                          />
                          <div className="acoris-recap__info-content">
                            <span className="acoris-recap__info-label">
                              {label}
                            </span>
                            {typeof value === "string" ? (
                              <span className="acoris-recap__info-value">
                                {value}
                              </span>
                            ) : (
                              <div className="acoris-recap__info-value acoris-recap__info-value--stack">
                                {value}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="acoris-recap__info-edit"
                          onClick={() => handleEdit(template, isBeforeLocked)}
                          aria-label={translate?.EDIT}
                        >
                          <img src={editSquareIcon} alt="" />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="acoris-recap__section acoris-recap__section--spaced">
                <p className="acoris-recap__section-label">
                  {translate?.INFO?.EFFECTIVE_DATE_TITLE}
                </p>
                <div className="acoris-recap__info-item">
                  <div className="acoris-recap__info-main">
                    <img
                      src={eventUpcomingIcon}
                      alt=""
                      className="acoris-recap__info-icon"
                    />
                    <div className="acoris-recap__info-content">
                      <span className="acoris-recap__info-label">
                        {translate?.INFO?.EFFECTIVE_DATE}
                      </span>
                      <span className="acoris-recap__info-value">
                        {moment(
                          get(subscription, "project.contract.issuanceDate"),
                        ).format(DATE_FORMAT)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="acoris-recap__info-edit"
                    onClick={() =>
                      handleEdit(
                        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_ACORIS]
                          .EFFECTIVE_DATE,
                        true,
                      )
                    }
                    aria-label={translate?.EDIT}
                  >
                    <img src={editSquareIcon} alt="" />
                  </button>
                </div>
              </div>

              <div className="acoris-recap__notice">
                <p
                  dangerouslySetInnerHTML={{
                    __html: translate?.NOTICE?.WITHDRAWAL || "",
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <hr className="acoris-recap__consent-divider" />

        <div className="acoris-recap__consent">
          <p className="acoris-recap__consent--intro">
            {translate?.CONSENT?.INTRO}
          </p>
          <div className="acoris-recap__consent--checkboxes">
            {consentCheckboxes.map(({ field, label }) => (
              <Checkbox
                key={field}
                variant="round"
                checked={get(values, field) || false}
                onChange={(checked) => setFieldValue(field, checked)}
              >
                {label}
              </Checkbox>
            ))}
          </div>
        </div>

        <ActionButtons
          onBack={onBack}
          onNext={submitForm}
          backText={translate?.CTA?.BACK}
          nextText={translate?.CTA?.SIGN}
          nextProps={{
            disabled: !allConsentsAccepted || documentsLoading,
            loading: documentsLoading,
          }}
        />
      </div>
    </Layout>
  );
}

export default View;
